/**
 * Utility functions for appointment form handling
 * Provides reusable logic for category management, appointment type filtering,
 * and dependent field management
 */

import type { AppointmentTypeVariant } from '../hooks/useCptAddonSelection';
import { apiFetch, buildApiUrl } from './apiClient';
import { CPT_ADDON_PAIRS, CPT_UNIT_TIMES } from '../features/setup-wizard/constants/cptCodes';
import {
  CATEGORY_LABELS as CANONICAL_CATEGORY_LABELS,
  CATEGORY_GROUP_LABELS,
  ALL_CATEGORY_OPTIONS,
} from '../features/setup-wizard/constants/appointmentTypes';

/** Fields that can be reset when appointment category changes */
interface CategoryResetFields {
  appointment_type_id: string;
  subtype: string;
  patient_ids?: string[];
  cpt_code_id?: string;
  fee?: string;
  is_payment_on_file?: boolean;
  has_outstanding_balance?: boolean;
  use_incident_to_billing?: boolean;
  bill_as_supervisor?: boolean;
  supervisor_name?: string;
  supervisor_npi?: string;
  provider_participation?: unknown[];
}

/** Default form values for an appointment */
interface AppointmentDefaults {
  duration: number;
  modality: string;
  add_to_timesheet: boolean;
  location_id?: string;
}

/** Validation errors for appointment form */
interface AppointmentValidationErrors {
  category?: string;
  appointment_type_id?: string;
  date?: string;
  time?: string;
  duration?: string;
  patient_ids?: string;
}

/** Auto-fill fields from appointment type */
interface AutoFillFields {
  duration?: number;
  cpt_code_id?: string;
  fee?: string;
  subtype?: string;
  buffer_minutes_after?: number | null;
  appointment_type_variant_id?: string;
  units?: number;
  billing_unit?: 'appointment' | 'attending_client';
}

/**
 * Resolve the canonical appointment type variant for a booking form.
 * - Single active variant: auto-select.
 * - Multiple active variants: prefer the unique default, then an exact
 *   duration match, then (for categories that default to 60 min) a unique
 *   60-minute variant.  Never silently pick the first of several ambiguous
 *   variants.
 */
export function resolveAppointmentTypeVariant(
  variants: AppointmentTypeVariant[],
  duration: number | null,
  prefer60 = false,
): AppointmentTypeVariant | null {
  const active = variants.filter((v: AppointmentTypeVariant) => v.is_active);
  if (active.length === 0) return null;
  if (active.length === 1) return active[0];

  if (prefer60) {
    const sixtyMin = active.filter((v) => v.duration_minutes === 60);
    if (sixtyMin.length === 1) return sixtyMin[0];
  }

  const defaults = active.filter((v) => v.is_default);
  if (defaults.length === 1) return defaults[0];

  if (duration) {
    const exactMatches = active.filter((v) => v.duration_minutes === duration);
    if (exactMatches.length === 1) return exactMatches[0];
  }

  return null;
}

/**
 * Fallback appointment categories when no appointment type data is available.
 */
export const APPOINTMENT_CATEGORIES = [
  { value: 'clinical', label: 'Clinical' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'admin_client_contact', label: 'Admin Client Contact' },
  { value: 'internal_meeting', label: 'Internal Meeting' },
  { value: 'external_meeting', label: 'External Meeting' },
  { value: 'admin_time', label: 'Admin Time' },
  { value: 'time_off', label: 'Time Off' },
  { value: 'break', label: 'Break' },
  { value: 'misc_appt', label: 'Misc Appt' },
  { value: 'calendar_note', label: 'Calendar Note' },
];

/** Labels for encounter-level category groups. */
const GROUP_LABELS: Record<string, string> = {
  clinical: 'Clinical',
  assessment: 'Assessment',
  admin_client_contact: 'Admin Client Contact',
  internal_meeting: 'Internal Meeting',
  external_meeting: 'External Meeting',
  admin_time: 'Admin Time',
  admin: 'Admin / Scheduling',
  time_off: 'Time Off',
  break: 'Break',
  misc_appt: 'Misc Appt',
  calendar_note: 'Calendar Note',
};

/** Sort weight for category groups in the dropdown. */
export const CATEGORY_GROUP_ORDER: Record<string, number> = {
  clinical: 0,
  assessment: 5,
  admin_client_contact: 10,
  internal_meeting: 20,
  external_meeting: 21,
  admin_time: 30,
  admin: 31,
  time_off: 40,
  break: 41,
  misc_appt: 50,
  calendar_note: 60,
};

/** Structural categories that always appear in the dropdown regardless of
 *  whether the org has appointment types for them (users create time-off,
 *  breaks, etc. directly — they don't need pre-existing appointment types). */
const ALWAYS_VISIBLE_GROUPS = new Set([
  'admin_time', 'time_off', 'break', 'internal_meeting', 'external_meeting',
  'misc_appt', 'calendar_note',
]);

/**
 * Build category dropdown options dynamically from appointment type data.
 * Uses the `category_group` field returned by the backend (resolved per-org).
 * Structural categories (time_off, break, etc.) are always included so users
 * can create those appointment kinds even when no types exist yet.
 * Falls back to APPOINTMENT_CATEGORIES when no types are available.
 */
export function buildCategoriesFromTypes(
  appointmentTypes: Array<{ category_group?: string }>,
): Array<{ value: string; label: string }> {
  if (!appointmentTypes?.length) return [...APPOINTMENT_CATEGORIES];

  const seen = new Set<string>();
  const categories: Array<{ value: string; label: string }> = [];

  // Add groups from actual appointment type data
  for (const type of appointmentTypes) {
    const group = type.category_group;
    if (!group || seen.has(group)) continue;
    seen.add(group);
    categories.push({
      value: group,
      label: GROUP_LABELS[group] || group.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    });
  }

  // Ensure structural categories are always present
  for (const group of ALWAYS_VISIBLE_GROUPS) {
    if (!seen.has(group)) {
      categories.push({
        value: group,
        label: GROUP_LABELS[group] || group.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      });
    }
  }

  return categories.sort((a, b) => {
    const orderA = CATEGORY_GROUP_ORDER[a.value] ?? 50;
    const orderB = CATEGORY_GROUP_ORDER[b.value] ?? 50;
    return orderA - orderB || a.label.localeCompare(b.label);
  });
}

/**
 * Lookup map from category value to human-readable label.
 * Derived from APPOINTMENT_CATEGORIES — single source of truth.
 */
export const CATEGORY_LABEL_MAP: Record<string, string> = Object.fromEntries(
  APPOINTMENT_CATEGORIES.map(c => [c.value, c.label])
);

/**
 * Canonical sub-category ("granular category") ordering, derived from
 * ALL_CATEGORY_OPTIONS — the single source of truth in the setup-wizard
 * constants, shared with the admin appointment-type config UIs. Each clinic's
 * custom categories (not in the canonical list) sort to the end and fall back
 * to alphabetical ordering by label.
 */
const CANONICAL_CATEGORY_ORDER: Record<string, number> = Object.fromEntries(
  ALL_CATEGORY_OPTIONS.map((opt, index) => [opt.value, index]),
);

/** De-slugify a category/group slug (e.g. `family_intake` → `Family Intake`). */
function humanizeSlug(slug: string): string {
  return slug.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Sort weight for a granular (sub-)category. Unknown/custom categories → end. */
export function granularCategoryOrder(category: string): number {
  return CANONICAL_CATEGORY_ORDER[category] ?? 999;
}

/**
 * Human-readable label for a top-level category group. Prefers the shared
 * `CATEGORY_GROUP_LABELS`, then the encounter-level `GROUP_LABELS`, then
 * de-slugifies a clinic's custom group key.
 */
export function categoryGroupLabel(group: string): string {
  return CATEGORY_GROUP_LABELS[group] || GROUP_LABELS[group] || humanizeSlug(group);
}

/**
 * Human-readable label for a granular (sub-)category. Prefers the shared
 * canonical `CATEGORY_LABELS`, then de-slugifies the clinic's custom category
 * slug so custom sub-categories still render a readable name.
 */
export function granularCategoryLabel(category: string): string {
  return CANONICAL_CATEGORY_LABELS[category] || humanizeSlug(category);
}

/** Minimal shape needed to group an appointment type by category/sub-category. */
export interface GroupableAppointmentType {
  category?: string;
  category_group?: string;
}

/** A granular sub-category bucket of appointment types. */
export interface AppointmentTypeSubgroup<T> {
  category: string;
  categoryLabel: string;
  types: T[];
}

/** A top-level category-group bucket containing sub-category buckets. */
export interface AppointmentTypeGroup<T> {
  group: string;
  groupLabel: string;
  subgroups: Array<AppointmentTypeSubgroup<T>>;
}

/**
 * Group a flat list of appointment types by top-level `category_group`
 * ("category") and then by granular `category` ("sub-category") — mirroring the
 * encounter modal's grouped type dropdown. Groups and sub-groups are sorted by
 * their canonical display order.
 */
export function groupAppointmentTypesByCategory<T extends GroupableAppointmentType>(
  appointmentTypes: T[],
): Array<AppointmentTypeGroup<T>> {
  if (!appointmentTypes?.length) return [];

  const groupMap = new Map<string, Map<string, T[]>>();
  for (const type of appointmentTypes) {
    const category = type.category || 'other';
    const group = type.category_group || category;
    if (!groupMap.has(group)) groupMap.set(group, new Map());
    const subMap = groupMap.get(group)!;
    if (!subMap.has(category)) subMap.set(category, []);
    subMap.get(category)!.push(type);
  }

  return Array.from(groupMap.entries())
    .map(([group, subMap]) => ({
      group,
      groupLabel: categoryGroupLabel(group),
      subgroups: Array.from(subMap.entries())
        .map(([category, types]) => ({
          category,
          categoryLabel: granularCategoryLabel(category),
          types,
        }))
        .sort(
          (a, b) =>
            granularCategoryOrder(a.category) - granularCategoryOrder(b.category) ||
            a.categoryLabel.localeCompare(b.categoryLabel),
        ),
    }))
    .sort(
      (a, b) =>
        (CATEGORY_GROUP_ORDER[a.group] ?? 50) - (CATEGORY_GROUP_ORDER[b.group] ?? 50) ||
        a.groupLabel.localeCompare(b.groupLabel),
    );
}

/**
 * Normalize category value to match backend enum format
 * Handles case variations and ensures consistent format
 * 
 * @param {string} category - The category value to normalize
 * @returns {string} Normalized category value
 */
export function normalizeCategory(category) {
  if (!category) return '';
  return category.toLowerCase().trim();
}

/**
 * Filter appointment types by category group.
 * Uses the `category_group` field from the backend (resolved per-org).
 */
export function filterAppointmentTypesByCategory(appointmentTypes, selectedCategory) {
  if (!selectedCategory || !appointmentTypes) {
    return appointmentTypes || [];
  }

  const normalizedCategory = normalizeCategory(selectedCategory);

  return appointmentTypes.filter(type => {
    if (!type.category) return false;
    const group = normalizeCategory(type.category_group || type.category);
    return group === normalizedCategory;
  });
}

/**
 * Infer the clinical session context from an appointment type name.
 * Appointment types describe the workflow, while session_type captures who
 * the session is for.
 */
export function getSessionTypeFromAppointmentType(
  appointmentType: { name?: string } | null | undefined,
): string | undefined {
  const normalizedName = appointmentType?.name?.toLowerCase().trim() || '';
  if (normalizedName.includes('couples therapy')) return 'couples';
  return undefined;
}

/**
 * Check if a category requires patient selection
 * Clinical and admin client contact appointments require patients
 * 
 * @param {string} category - The appointment category
 * @returns {boolean} True if patient is required
 */
export function categoryRequiresPatient(category) {
  const normalizedCategory = normalizeCategory(category);
  return normalizedCategory === 'clinical' || normalizedCategory === 'admin_client_contact' || normalizedCategory === 'assessment';
}

/**
 * Check if a category supports fee/billing
 * Clinical and assessment appointments have fees
 *
 * @param {string} category - The appointment category
 * @returns {boolean} True if category supports fees
 */
export function categorySupportsFees(category, appointmentType = null) {
  const normalizedCategory = normalizeCategory(category);
  if (normalizedCategory === 'clinical' || normalizedCategory === 'assessment') return true;
  return normalizedCategory === 'admin_client_contact' && Boolean(
    appointmentType?.billable
      || appointmentType?.variants?.some((variant) => variant.billable),
  );
}

/** Fallback while the patient-facing categories fetch is in flight or fails. */
export const PATIENT_FACING_CATEGORIES_FALLBACK: ReadonlySet<string> = new Set([
  'clinical',
  'admin_client_contact',
  'yoga',
  'wellness',
  'patient_service',
]);

let _patientFacingCategoriesPromise: Promise<Set<string>> | null = null;

/**
 * Appointment categories that involve a patient (backend CATEGORIES_REQUIRING_PATIENT,
 * served by /metadata/patient-facing-categories). Shared by the appointment preview
 * popover and encounter-edit type filters. Fetched once per page load; resolves to
 * the fallback set on failure.
 */
export function getPatientFacingCategories(): Promise<Set<string>> {
  if (!_patientFacingCategoriesPromise) {
    _patientFacingCategoriesPromise = apiFetch(
      buildApiUrl('/api/calendar/appointments/metadata/patient-facing-categories'),
      { method: 'GET', credentials: 'include' }
    )
      .then((res) => res.json())
      .then((data: string[]) =>
        Array.isArray(data) && data.length ? new Set(data) : new Set(PATIENT_FACING_CATEGORIES_FALLBACK)
      )
      .catch(() => new Set(PATIENT_FACING_CATEGORIES_FALLBACK));
  }
  return _patientFacingCategoriesPromise;
}

/**
 * Check if a category supports CPT codes
 * Only clinical appointments use CPT codes
 * 
 * @param {string} category - The appointment category
 * @returns {boolean} True if category supports CPT codes
 */
export function categorySupportsCptCodes(category, appointmentType = null) {
  const normalizedCategory = normalizeCategory(category);
  if (normalizedCategory === 'clinical' || normalizedCategory === 'assessment') return true;
  return normalizedCategory === 'admin_client_contact' && Boolean(
    appointmentType?.cpt_codes?.length
      || appointmentType?.default_cpt_code_id
      || appointmentType?.variants?.some(
        (variant) => variant.default_cpt_code_id || variant.cpt_codes?.length,
      ),
  );
}

/**
 * Check if a category supports multiple attendees
 * Only internal meetings support multiple attendees
 * 
 * @param {string} category - The appointment category
 * @returns {boolean} True if category supports multiple attendees
 */
export function categorySupportsMultipleAttendees(category) {
  const normalizedCategory = normalizeCategory(category);
  return normalizedCategory === 'internal_meeting';
}

/**
 * Get fields that should be reset when category changes
 * Returns an object with fields to reset based on the new category
 * 
 * @param {string} newCategory - The newly selected category
 * @param {Object} currentFormData - Current form data
 * @returns {Object} Fields to reset
 */
export function getFieldsToResetOnCategoryChange(newCategory, currentFormData): CategoryResetFields {
  const fieldsToReset: CategoryResetFields = {
    appointment_type_id: '',
    subtype: ''
  };

  if (!categorySupportsMultipleAttendees(newCategory)) {
    if (currentFormData.provider_participation && currentFormData.provider_participation.length > 1) {
      fieldsToReset.provider_participation = currentFormData.provider_participation.slice(0, 1);
    }
  }

  return fieldsToReset;
}

/**
 * Get default form values based on category
 * Provides sensible defaults for different appointment types
 * 
 * @param {string} category - The appointment category
 * @param {Object} userDefaults - User's default settings (location, etc.)
 * @returns {Object} Default form values
 */
export function getDefaultFormValuesForCategory(category, userDefaults: Record<string, unknown> = {}): AppointmentDefaults {
  const defaults: AppointmentDefaults = {
    duration: 60, // Default 60 minutes
    modality: 'in_person',
    add_to_timesheet: true
  };

  const normalizedCategory = normalizeCategory(category);

  switch (normalizedCategory) {
    case 'clinical':
      defaults.duration = 60;
      defaults.add_to_timesheet = true;
      break;

    case 'internal_meeting':
      defaults.duration = 30;
      defaults.add_to_timesheet = true;
      break;

    case 'admin_time':
      defaults.duration = 60;
      defaults.add_to_timesheet = true;
      break;

    case 'time_off':
      defaults.duration = 480; // 8 hours
      defaults.add_to_timesheet = false;
      break;

    case 'calendar_note':
      defaults.duration = 0;
      defaults.add_to_timesheet = false;
      break;

    default:
      defaults.duration = 60;
  }

  if (userDefaults.default_location_id) {
    defaults.location_id = userDefaults.default_location_id as string;
  }

  return defaults;
}

/**
 * Validate form data based on category requirements
 * Returns an object with validation errors
 * 
 * @param {Object} formData - The form data to validate
 * @returns {Object} Validation errors (empty object if valid)
 */
export function validateFormByCategory(formData): AppointmentValidationErrors {
  const errors: AppointmentValidationErrors = {};

  if (!formData.category) {
    errors.category = 'Category is required';
  }

  if (!formData.appointment_type_id) {
    errors.appointment_type_id = 'Appointment type is required';
  }

  if (!formData.date) {
    errors.date = 'Date is required';
  }

  if (!formData.time) {
    errors.time = 'Time is required';
  }

  if (!formData.duration || formData.duration <= 0) {
    errors.duration = 'Duration must be greater than 0';
  }

  if (categoryRequiresPatient(formData.category)) {
    if (!formData.patient_ids || formData.patient_ids.length === 0) {
      errors.patient_ids = 'Patient is required for this appointment type';
    }
  }

  return errors;
}

/**
 * Calculate add-on CPT code and units from a primary CPT code and duration.
 * For first-unit/additional-unit patterns (e.g., 96136 first 30 min → 96137
 * each additional 30 min), returns the add-on code and calculated units.
 * Returns null if no add-on pair exists or duration doesn't exceed base unit.
 */
export function calculateAddonFromDuration(
  primaryCptCode: string,
  durationMinutes: number,
): { code: string; units: number } | null {
  const addonCode = CPT_ADDON_PAIRS[primaryCptCode];
  if (!addonCode) return null;

  const baseUnitTime = CPT_UNIT_TIMES[primaryCptCode];
  const addonUnitTime = CPT_UNIT_TIMES[addonCode];
  if (!baseUnitTime || !addonUnitTime) return null;

  const remainingMinutes = durationMinutes - baseUnitTime;
  if (remainingMinutes <= 0) return null;

  const units = Math.floor(remainingMinutes / addonUnitTime);
  if (units <= 0) return null;

  return { code: addonCode, units };
}

/**
 * Reverse calculation: given a primary CPT code and add-on units,
 * derive the implied appointment duration.
 * E.g., 96136 + 3 units of 96137 → 30 + (3 × 30) = 120 min.
 * Returns null if the primary code has no add-on pair.
 */
export function calculateDurationFromAddonUnits(
  primaryCptCode: string,
  addonUnits: number,
): number | null {
  const addonCode = CPT_ADDON_PAIRS[primaryCptCode];
  if (!addonCode) return null;

  const baseUnitTime = CPT_UNIT_TIMES[primaryCptCode];
  const addonUnitTime = CPT_UNIT_TIMES[addonCode];
  if (!baseUnitTime || !addonUnitTime) return null;

  return baseUnitTime + addonUnits * addonUnitTime;
}

/**
 * Auto-fill appointment type details when selected.
 * Uses duration-based CPT matching when the type has duration_min/max mappings
 * (new canonical system). Falls back to is_default for legacy types.
 */
export function getAutoFillFieldsFromAppointmentType(appointmentType, currentFormData): AutoFillFields {
  if (!appointmentType) return {};

  const fieldsToUpdate: AutoFillFields = {
    cpt_code_id: '',
    units: 1,
    buffer_minutes_after: null,
    appointment_type_variant_id: '',
    billing_unit: appointmentType.billing_unit || 'appointment',
  };

  if (appointmentType.duration_minutes) {
    fieldsToUpdate.duration = appointmentType.duration_minutes;
  }

  const duration = fieldsToUpdate.duration || currentFormData.duration || null;
  const variants: AppointmentTypeVariant[] = appointmentType.variants ?? [];

  // Categories where the default duration should be 60 minutes when a
  // 60-min variant exists (e.g. "Consult Group" types that ship with both
  // 60- and 120-min variants but should default to the shorter session).
  const PREFER_60_MIN_CATEGORIES = ['consultation', 'group_therapy'];
  const typeCategory = (appointmentType.category || '').toLowerCase();
  const prefer60 = PREFER_60_MIN_CATEGORIES.includes(typeCategory);

  const activeVariants = variants.filter((v: AppointmentTypeVariant) => v.is_active);
  const defaultVariant = resolveAppointmentTypeVariant(activeVariants, duration, prefer60);

  if (defaultVariant) {
    fieldsToUpdate.appointment_type_variant_id = defaultVariant.id;
    fieldsToUpdate.duration = defaultVariant.duration_minutes;
    if (defaultVariant.default_cpt_code_id && categorySupportsCptCodes(currentFormData.category)) {
      fieldsToUpdate.cpt_code_id = defaultVariant.default_cpt_code_id;
    }
    if (defaultVariant.default_units) {
      fieldsToUpdate.units = defaultVariant.default_units;
    }
    if (defaultVariant.buffer_minutes != null) {
      fieldsToUpdate.buffer_minutes_after = defaultVariant.buffer_minutes;
    }
  } else if (categorySupportsCptCodes(currentFormData.category) && appointmentType.cpt_codes?.length) {
    // Fall back to CPT mapping range match when no variants exist
    fieldsToUpdate.appointment_type_variant_id = '';
    const primaryCodes = appointmentType.cpt_codes.filter(
      (c: { is_addon?: boolean }) => !c.is_addon
    );

    const hasDurationMappings = primaryCodes.some(
      (c: { duration_min_minutes?: number | null; duration_max_minutes?: number | null }) =>
        c.duration_min_minutes != null || c.duration_max_minutes != null
    );

    let matched = false;
    if (hasDurationMappings && duration) {
      for (const cpt of primaryCodes) {
        const min = cpt.duration_min_minutes as number | null | undefined;
        const max = cpt.duration_max_minutes as number | null | undefined;
        if (min == null && max == null) continue;

        const aboveMin = min == null || duration >= min;
        const belowMax = max == null || duration <= max;
        if (aboveMin && belowMax && cpt.id) {
          fieldsToUpdate.cpt_code_id = cpt.id;
          matched = true;
          break;
        }
      }
    }

    if (!matched) {
      const defaultPrimary = primaryCodes.find(
        (c: { is_default?: boolean; id?: string }) => c.is_default
      );
      if (defaultPrimary?.id) {
        fieldsToUpdate.cpt_code_id = defaultPrimary.id;
      }
    }

  }

  if (appointmentType.subtype) {
    fieldsToUpdate.subtype = appointmentType.subtype;
  }

  // Only use type-level buffer as fallback when variant didn't provide one
  if (fieldsToUpdate.buffer_minutes_after == null
    && appointmentType.buffer_minutes_after !== undefined
    && appointmentType.buffer_minutes_after !== null) {
    fieldsToUpdate.buffer_minutes_after = appointmentType.buffer_minutes_after;
  }

  return fieldsToUpdate;
}

export interface MeetingAttendeeOption {
  id: string;
  user_id?: string | null;
  is_staff_user?: boolean;
}

export interface MeetingRosterEntry {
  practitioner_id?: string;
  user_id?: string;
}

/** A user_id roster entry also matches that user's practitioner option. */
export function meetingAttendeeMatches(
  option: MeetingAttendeeOption,
  entry: MeetingRosterEntry,
): boolean {
  return entry.user_id
    ? option.user_id === entry.user_id
    : !option.is_staff_user && option.id === entry.practitioner_id;
}
